import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import { api } from '../services/api';
import { Bold, Italic, Underline, List, ListOrdered, Link2 } from 'lucide-react';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 0.6rem 0.4rem;
    font-size: 0.9rem;
  }

  th {
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }
`;

const Select = styled.select`
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid #d6dae0;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
`;

const ReplyForm = styled.form`
  display: grid;
  gap: 0.75rem;
`;

const ReplyGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  grid-column: span ${props => props.$span ?? 1};

  @media (max-width: 900px) {
    grid-column: 1 / -1;
  }
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  width: 100%;
`;

const EditorWrapper = styled.div`
  border: 1px solid #d6dae0;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.5rem;
  background: #eef2f7;
  border-bottom: 1px solid #d6dae0;
`;

const ToolButton = styled.button`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.35rem 0.45rem;
  cursor: pointer;
  color: #334155;
`;

const Editor = styled.div`
  min-height: 220px;
  padding: 0.85rem;
  outline: none;
  font-size: 0.95rem;
  line-height: 1.6;
  background: #ffffff;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: #0a0e17;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
`;

const GhostButton = styled.button`
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 0.6rem 0.9rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
`;

const Badge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $status }) => {
    switch ($status) {
      case 'resolved':
        return 'rgba(34, 197, 94, 0.15)';
      case 'in_progress':
        return 'rgba(251, 191, 36, 0.2)';
      default:
        return 'rgba(239, 68, 68, 0.12)';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'resolved':
        return '#15803d';
      case 'in_progress':
        return '#b45309';
      default:
        return '#b91c1c';
    }
  }};
`;

export const AdminContactPage: React.FC = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const editorRef = useRef<HTMLDivElement | null>(null);

  const statusLabel = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'Resolvido';
      case 'in_progress':
        return 'Em andamento';
      case 'new':
      default:
        return 'Novo';
    }
  };

  const loadMessages = async () => {
    const response = await api.get('/admin/contact/messages');
    setMessages(response.data.data || []);
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const handleStatusChange = async (messageId: number, status: string) => {
    await api.put(`/admin/contact/messages/${messageId}`, { status });
    loadMessages();
  };

  const handleSelect = (message: any) => {
    setSelectedMessage(message);
    setReplySubject(message.reply_subject || `Re: ${message.subject}`);
    setReplyBody(message.reply_body || '');
    if (editorRef.current) {
      editorRef.current.innerHTML = message.reply_body || '';
    }
  };

  const handleReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedMessage) return;
    await api.post(`/admin/contact/messages/${selectedMessage.id}/reply`, {
      reply_subject: replySubject,
      reply_body: replyBody,
    });
    setReplyBody('');
    setSelectedMessage(null);
    loadMessages();
  };

  const exec = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setReplyBody(editorRef.current.innerHTML || '');
    }
  };

  const handleEditorInput = () => {
    if (!editorRef.current) return;
    setReplyBody(editorRef.current.innerHTML || '');
  };

  return (
    <AdminLayout>
      <PageHeader>
        <Title>Contato</Title>
      </PageHeader>

      <Card>
        <Table>
          <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Assunto</th>
                <th>Mensagem</th>
                <th>Status</th>
                <th>Atualizar</th>
                <th>Responder</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(message => (
                <tr key={message.id}>
                  <td>{message.name}</td>
                  <td>{message.email}</td>
                  <td>{message.subject}</td>
                  <td>{message.message}</td>
                  <td><Badge $status={message.status}>{statusLabel(message.status)}</Badge></td>
                  <td>
                    <Select value={message.status} onChange={event => handleStatusChange(message.id, event.target.value)}>
                      <option value="new">Novo</option>
                      <option value="in_progress">Em andamento</option>
                      <option value="resolved">Resolvido</option>
                    </Select>
                  </td>
                  <td>
                    <GhostButton type="button" onClick={() => handleSelect(message)}>Responder</GhostButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        {selectedMessage && (
          <Card>
            <ReplyForm onSubmit={handleReply}>
              <ReplyGrid>
                <Field>
                  <Label>Responder para</Label>
                  <Input value={selectedMessage.email} readOnly />
                </Field>
                <Field>
                  <Label>Assunto</Label>
                  <Input value={replySubject} onChange={event => setReplySubject(event.target.value)} />
                </Field>
                <Field $span={2}>
                  <Label>Mensagem</Label>
                  <EditorWrapper>
                    <Toolbar>
                      <ToolButton type="button" onClick={() => exec('bold')}><Bold size={16} /></ToolButton>
                      <ToolButton type="button" onClick={() => exec('italic')}><Italic size={16} /></ToolButton>
                      <ToolButton type="button" onClick={() => exec('underline')}><Underline size={16} /></ToolButton>
                      <ToolButton type="button" onClick={() => exec('insertUnorderedList')}><List size={16} /></ToolButton>
                      <ToolButton type="button" onClick={() => exec('insertOrderedList')}><ListOrdered size={16} /></ToolButton>
                      <ToolButton type="button" onClick={() => {
                        const url = window.prompt('URL do link');
                        if (url) exec('createLink', url);
                      }}>
                        <Link2 size={16} />
                      </ToolButton>
                    </Toolbar>
                    <Editor
                      ref={editorRef}
                      contentEditable
                      onInput={handleEditorInput}
                      suppressContentEditableWarning
                    />
                  </EditorWrapper>
                </Field>
              </ReplyGrid>
              <ButtonRow>
                <PrimaryButton type="submit">Enviar resposta</PrimaryButton>
                <GhostButton type="button" onClick={() => setSelectedMessage(null)}>Cancelar</GhostButton>
              </ButtonRow>
            </ReplyForm>
          </Card>
        )}
      </AdminLayout>
  );
};
